import React, { useCallback, useEffect, useState } from 'react';
import { url, authFetch } from './apiClient';
import { formatDate } from './dateFormat';
import { monthLabel, rupees } from './dashboardStats';
import './Dashboard.css';

// Money the owner paid for the building (water tanker, pumping electricity,
// cleaning, maintenance). The dashboard subtracts these from rent collected
// to show the month's profit. Only the admin sees these.

export const EXPENSE_CATEGORIES = [
  { value: 'WATER', label: 'Water tanker' },
  { value: 'PUMPING', label: 'Pumping electricity' },
  { value: 'CLEANING', label: 'Cleaning' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'OTHER', label: 'Other' },
];
const categoryLabel = (v) => (EXPENSE_CATEGORIES.find((c) => c.value === v) || { label: v }).label;

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const EMPTY = { date: today(), category: 'WATER', amount: '', note: '' };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid var(--line-strong)',
  borderRadius: 8, fontSize: 15, fontFamily: 'inherit', background: '#fff', color: 'var(--ink)' };

export default function AdminExpenses() {
  const [month, setMonth] = useState(today().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await authFetch(url.adminExpenses(month));
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data.error || 'Could not load expenses');
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    }
  }, [month]);

  useEffect(() => { if (month) load(); }, [month, load]);

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authFetch(url.adminExpenses(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not save');
      setForm({ ...EMPTY, date: form.date, category: form.category });
      if (data.monthYear === month) load(); else setMonth(data.monthYear);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${categoryLabel(row.category)} ${rupees(row.amount)} on ${formatDate(row.date)}?`)) return;
    const res = await authFetch(url.adminExpense(row.id), { method: 'DELETE' });
    if (!res.ok) { alert('Could not delete'); return; }
    setRows((r) => r.filter((x) => x.id !== row.id));
  };

  const total = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  return (
    <div className="dash">
      <div className="dash-head">
        <div>
          <h2 className="dash-title">Expenses</h2>
          <p className="dash-sub">Enter what you paid for the building: water tanker, pumping electricity, cleaning or maintenance. The dashboard takes these away from rent collected to show your profit.</p>
        </div>
        <div className="dash-actions">
          <label className="field-inline">
            <span>Month</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </label>
        </div>
      </div>

      {error && <div className="alert alert--danger">{error}</div>}

      <form className="panel" onSubmit={add}>
        <div className="panel__head"><h3>Add expense</h3></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 8 }}>
          <label style={{ fontSize: 13 }}>Date paid
            <input type="date" required value={form.date} onChange={(e) => setField('date', e.target.value)} style={inputStyle} />
          </label>
          <label style={{ fontSize: 13 }}>For
            <select value={form.category} onChange={(e) => setField('category', e.target.value)} style={inputStyle}>
              {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 13 }}>Amount ₹
            <input type="number" required min="1" step="any" inputMode="decimal" placeholder="0"
              value={form.amount} onChange={(e) => setField('amount', e.target.value)} style={inputStyle} />
          </label>
        </div>
        <input type="text" maxLength={500} placeholder="Note (optional)" value={form.note}
          onChange={(e) => setField('note', e.target.value)} style={{ ...inputStyle, marginBottom: 8 }} />
        <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Add expense'}</button>
      </form>

      <div className="panel">
        <div className="panel__head">
          <h3>{monthLabel(month)}</h3>
          <strong>Total {rupees(total)}</strong>
        </div>
        {rows.length === 0 ? (
          <p className="muted">No expenses for this month yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Date</th><th>For</th><th>Note</th><th className="num">Amount</th><th /></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDate(r.date)}</td>
                    <td>{categoryLabel(r.category)}</td>
                    <td className="muted">{r.note}</td>
                    <td className="num"><strong>{rupees(r.amount)}</strong></td>
                    <td className="num">
                      <button type="button" className="btn btn--ghost" style={{ padding: '4px 10px' }} onClick={() => remove(r)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
